import { useQuery } from '@apollo/react-hooks';
import { Button, Spin } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { BUNCHBALL_ENDPOINT } from '../../../endpoints';
import { BUNCHBALL_ENV, Environment } from '../../../environments';
import { BB_TOKEN_QUERY } from '../../../gql/queries/BB_TOKEN_QUERY';
import { useAuthState } from '../../../hooks/auth';
import { useTranslations } from '../../../hooks/translations';
import useScript from '../../../hooks/useScript';
import { Translate, translationService } from '../../../services';
import { getInlineStyle } from '../../../styles/inlineStyles';
import { PageContainer, PageContent } from '../../common';
import './styles.less';
import { getString } from '@nackle/intl-tools';

const activityTabs = {
    personal: 'personal',
    community: 'community',
};

const Progress = () => {

    const { locale } = useTranslations();

    useScript( `https://components.bunchball.com/web/${ BUNCHBALL_ENV === Environment.QA ? 'latest' : 'v6.1' }/component.min.js` );
    const { claims } = useAuthState();

    const [ currentTab, setCurrentTab ] = useState( activityTabs.personal );
    const [ leaderboards, setLeaderboards ] = useState( [] );
    const [ groupClassId, setGroupClassId ] = useState( undefined );

    const [ hideCommunityBoards, setHideCommunityBoards ] = useState( false );

    const [ translationKey, setTranslationKey ] = useState( );

    const [ loading, setLoading ] = useState( false );

    const { data: bbTokenData } = useQuery( BB_TOKEN_QUERY, { fetchPolicy: 'network-only' } );
    const dataToken = useMemo( () => bbTokenData?.bunchballToken.accessToken, [ bbTokenData ] );
    

    const handleManualLocaleUpdate = () => {
        setLoading( true );
        setTimeout( () => setLoading( false ), 2000 );
    };

    useEffect( () => {
        const key = translationService.attachProgressTranslationBundle();

        setTranslationKey( key );

        translationService.on( translationService.events.MANUAL_UPDATE, handleManualLocaleUpdate );

        if ( claims.locale == 'de-DE' ) {
            // weird rule where users who are assigned german locale should not see community boards
            setHideCommunityBoards( true );
            setCurrentTab( activityTabs.personal );
        }

        return () => {
            translationService.off( translationService.events.MANUAL_UPDATE, handleManualLocaleUpdate );
        };
    }, [] );

    useEffect( () => {
        if ( !dataToken || hideCommunityBoards ) {return;}

        // fetch all leaderboard IDs
        fetch( `${ BUNCHBALL_ENDPOINT }/leaderboards`, {
            headers: {
                authorization: `Bearer ${ dataToken }`
            },
            method: 'GET'
        } )
            .then( res => res.json() )
            .then( res => {
                setLeaderboards( res.response.map( l => ( { 
                    id: l.id, 
                    name: l.name.toLowerCase().includes( 'week' ) ? getString( 'progress.leaders.weeklyLeaderboardName' ) :
                        l.name.toLowerCase().includes( 'monthly' ) ? getString( 'progress.leaders.monthlyLeaderboardName' ) :
                        l.name.toLowerCase().includes( 'all-time' ) ? getString( 'progress.leaders.allTimeLeaderboardName' ) :
                        l.name, 
                    type: l.type 
                } ) ) );
            } )
            .catch( () => setLeaderboards( null ) );

        // fetch the BRand group class ID so users in different brands are secluded
        fetch( `${ BUNCHBALL_ENDPOINT }/group-classes?name=Brand`, {
            headers: {
                authorization: `Bearer ${ dataToken }`
            },
            method: 'GET',
        } )
            .then( res => res.json() )
            .then( res => setGroupClassId( res.response.map( g => g.id )?.[ 0 ] ) )
            .catch( () => setGroupClassId( null ) );
    }, [ dataToken, locale ] );

    const leaderboardConfig = useMemo( () => {
        if ( leaderboards?.length == 0 || groupClassId == undefined || hideCommunityBoards ) {return [];}

        const conf = leaderboards.map( l => ( {
            name: l.name,
            leaderboardId: l.id,
            groupClassIds: groupClassId ?? '',
            showProfile: 'true',
            criteria: l.type == 'point' ? 'balance' 
                : l.type == 'challenge' ? 'completionCount'
                : ''

        } ) );

        return JSON.stringify( conf );

    }, [ leaderboards, groupClassId ] );

    const inlineStyle = `
        .scrollable {
            width: 100%
        }

        img {
            border: none;
            border-radius: 100%;
        }

        h3 {
            font-weight: bold;
            font-size: 16px;
        }

        h5 {
            font-weight: bold;
            font-size: 24px;
        }

        .widget-header-title h3 {
            text-transform: capitalize
        }

        p {
            font-weight: bold;
        }

        div.editButton {
            border-radius: 10%;
            padding-left: 3px;
        }

        .editButton {
            color: #696969;
        }

        .crop-image {
            border-width: 0;
        }

        .grid-container > div {
            justify-content: space-between;
        }

        .grid-container .cell {
            margin: 0;
        }

        @media only screen and (max-width: 450px) {
            .nitro-widget > div, .nitro-widget .content {
                padding: 0 2px;
            }

            .nitro-widget::-webkit-scrollbar {
                display: none;
            }

            .nitro-widget {
                scrollbar-width: none;
            }
        }

        ${ getInlineStyle() }
    `;

    return (
        <PageContent className="page-progress">
            
            <PageContainer className="progress-page-container" >

            { !dataToken || loading ? <Spin /> :
                <>
                    <div className={ classNames( 'activity-tabs', { hidden: hideCommunityBoards } ) }>
                        <Button className={ classNames( { selected: currentTab === activityTabs.personal }, '_title' )  } onClick={ () => setCurrentTab( activityTabs.personal ) }><Translate.Text value="progress.personal" /></Button>
                        <Button className={ classNames( { selected: currentTab === activityTabs.community }, '_title' ) } onClick={ () => setCurrentTab( activityTabs.community ) }><Translate.Text value="progress.community" /></Button>
                    </div>

                    <div className={ classNames( 'progress-container', 'personal-activity', { hidden: currentTab !== activityTabs.personal } ) }>
                        <div className='activity-col'>
                            <div className='bb short'>
                                <bunchball-profile 
                                    data-token={ dataToken }
                                    data-server={ BUNCHBALL_ENDPOINT }
                                    data-user-id={ claims.person_id }
                                    data-balance-title='Award Account'
                                    data-editable-items='image'
                                    data-hide-level-progress='false'
                                    data-navigation='new tab'
                                    data-point-category='Points'
                                    data-language={ locale }
                                    data-translation-reference={ translationKey }
                                    data-inline-css={ `
                                    ${ inlineStyle } 

                                    .crop-image { 
                                        height: 100px; 
                                        width: 100px; 
                                    }

                                    .profile-image img {
                                        height: 100%;
                                    }

                                    .row {
                                        height: fit-content;
                                    }

                                    .row > div {
                                        height: fit-content;
                                    }

                                    #destination-waypoint, #level-section .label {
                                        display: none;
                                    }

                                    #level-section > div:nth-child(3), #level-section > div:nth-child(4) {
                                        margin-top: 15px;
                                    }

                                    #level-section #user-level-lable {
                                        margin: 0;
                                    }

                                    .editButton {
                                        padding: 0;
                                        margin: 5px;
                                    }

                                    #identity-elements > div:first-child {
                                        height: 100px;
                                    }

                                    .level-progress-bar line {
                                        stroke-width: 0.5px;
                                    }

                                    .level-progress-bar svg {
                                        margin-top: -50px;
                                        margin-bottom: -20px;
                                    }

                                    .profile #identity-elements {
                                        border-bottom: none;
                                    }

                                    .edit-view button.cancel-button {
                                        color: red;
                                    }

                                    .edit-view button.save-button {
                                        color: green;
                                    }
                                    ` }>
                                </bunchball-profile>
                            </div>
                            <div className='bb short'>
                                <bunchball-events 
                                    data-token={ dataToken }
                                    data-server={ BUNCHBALL_ENDPOINT }
                                    data-user-id={ claims.person_id }
                                    data-export-navigation='false'
                                    data-return-count='10'
                                    data-language={ locale }
                                    data-title={ window[ translationKey ][ locale ]?.translation.events.personal.title }
                                    data-empty-message={ window[ translationKey ][ locale ]?.translation.events.personal.emptyMessage }
                                    data-inline-css={ `
                                    ${ inlineStyle }

                                    .description {
                                        font-weight: bold;
                                    }

                                    .events {
                                        padding: 0 30px;
                                        max-height: 300px;
                                        overflow-y: auto;
                                    }

                                    .events .grid-row {
                                        min-height: 100px;
                                    }

                                    .events .grid-row .grid-x {
                                        min-height: 99px;
                                    }

                                    .grid-container > div > .cell:nth-child(1):not(.text-center) {
                                        width: fit-content;
                                    }

                                    .grid-container > div > .cell:nth-child(2) {
                                        max-width: 60%;
                                        flex-grow: 1;
                                    }

                                    @media only screen and (max-width: 450px) {

                                        .nitro-widget .events .grid-x {
                                            gap: 1rem;
                                        }
                                    }
                                    ` }>
                                </bunchball-events>
                            </div>
                        </div>
                        
                        

                        <div className='activity-col'>
                            <div className='bb'>
                                <bunchball-missions 
                                    data-token={ dataToken }
                                    data-server={ BUNCHBALL_ENDPOINT }
                                    data-user-id={ claims.person_id }
                                    data-hide-filter-expiring='false'
                                    data-hide-filter-inprogress='false'
                                    data-hide-filter-labels='false'
                                    data-log-click-through='false'
                                    data-navigation='inline'
                                    data-return-count='10'
                                    data-show-completed-missions='true'
                                    data-show-date-as-duration='true'
                                    data-show-eligible-missions='true'
                                    data-show-forced-label-filter='false'
                                    data-show-locked='all'
                                    data-language={ locale }
                                    data-translation-reference={ translationKey }
                                    data-inline-css={ `
                                    ${ inlineStyle } 

                                    div.content {
                                        margin: 0;
                                        padding: 0 1.8rem;
                                    }

                                    .nitro-widget .scrollable {
                                        overflow: hidden
                                    }

                                    .card {
                                        box-shadow: none;
                                        border-radius: 0;
                                        height: unset;

                                    }

                                    .circular-portrait .overlay-icon {
                                        outline: none;
                                        // this needs to be removed or else it looks awful in safari
                                    }

                                    .challenge-card {
                                        overflow: unset;
                                    }

                                    .card-stack {
                                        overflow-y: auto;
                                        max-height: 600px;
                                    }

                                    .card-stack .card {
                                        margin: 0;
                                    }

                                    .card-stack .card > div {
                                        justify-content: space-around;
                                    }

                                    .card-stack .loading-card {
                                        -webkit-box-shadow: none;
                                        box-shadow: none;
                                        margin-bottom: 0;
                                    }

                                    .card-stack .placeholder:nth-child(1) {
                                        margin-top: 20px;
                                    }

                                    .points {
                                        font-weight: bold;
                                        font-size: 38px;
                                        background-color: inherit;
                                    }

                                    .points-text .point-category {
                                        font-weight: bold;
                                        font-size: 12px;
                                    }

                                    .challenge-card .card-title {
                                        font-weight: bold;
                                        font-size: 16px;
                                    }

                                    .filters-responsive {
                                        column-gap: 17px;
                                        row-gap: 9px;
                                    }

                                    .filters-responsive button.filter {
                                        flex-grow: 1;
                                        max-width: 128px;
                                        margin: 0;
                                        z-index: 1;
                                        display: inline-flex;
                                        align-items: center;
                                    }

                                    .tabs {
                                        background: inherit;
                                    }

                                    .tasks-section .locked-section * {
                                        background-color: unset; 
                                    }
                                    ` }>
                                </bunchball-missions>
                            </div>    
                        </div> 


                        <div className='activity-col'>
                            <div className='bb'>
                                <bunchball-trophy 
                                    data-token={ dataToken }
                                    data-server={ BUNCHBALL_ENDPOINT }
                                    data-user-id={ claims.person_id }
                                    data-latest-only='false'
                                    data-language={ locale }
                                    data-translation-reference={ translationKey }
                                    data-inline-css={ `
                                    ${ inlineStyle }
                                    
                                    .nitro-trophy-widget .latest-trophy figure img {
                                        height: 120px;
                                        padding: 0;
                                        margin: 20px 0;
                                    }

                                    .nitro-trophy-widget .all-trophies figure img {
                                        height: 80px;
                                        padding: 0;
                                    }

                                    .nitro-trophy-widget .trophy-modal {
                                        z-index: 20;
                                    }

                                    .latest-trophy {
                                        margin-bottom: 30px;
                                        padding-bottom: 30px;
                                    }

                                    .nitro-trophy-widget section.all-trophies {
                                        border: none;
                                        overflow-y: auto;
                                        max-height: 450px;
                                    }

                                    .nitro-trophy-widget {
                                        font-family: inherit!important;
                                    }

                                    .nitro-trophy-widget figure.trophy img,
                                    .nitro-trophy-widget figure.latest-main img {
                                        object-fit: contain;
                                    }

                                    @media only screen and (max-width: 450px) {
                                        .nitro-trophy-widget .all-trophies figure {
                                            width: 50%;
                                        }
                                    }
                                    ` }>
                                </bunchball-trophy>
                            </div>
                        </div>

                    </div>

                    <div className={ classNames( 'progress-container', 'community-activity', { hidden: currentTab !== activityTabs.community } ) }>

                        <div className='bb tall'>
                            <bunchball-events 
                                data-token={ dataToken }
                                data-server={ BUNCHBALL_ENDPOINT }
                                data-user-id={ claims.person_id }
                                data-export-navigation='false'
                                data-group-class='Brand'
                                data-language={ locale }
                                data-title={ window[ translationKey ][ locale ]?.translation.events.community.title }
                                data-empty-message={ window[ translationKey ][ locale ]?.translation.events.community.emptyMessage }
                                data-return-count='10'
                                data-inline-css={ `
                                ${ inlineStyle }

                                .description {
                                    font-weight: bold;
                                }

                                .events {
                                    padding: 0 30px;
                                    overflow-y: auto;
                                    max-height: 750px;
                                }

                                .events .grid-row {
                                    min-height: 100px;
                                }

                                .events .grid-row .grid-x {
                                    min-height: 99px;
                                }

                                .grid-container > div > .cell:nth-child(1):not(.text-center) {
                                    width: fit-content;
                                }

                                .grid-container > div > .cell:nth-child(2) {
                                    max-width: 60%;
                                    flex-grow: 1;
                                }        
                                
                                @media only screen and (max-width: 450px) {

                                    .nitro-widget .events .grid-x {
                                        gap: 1rem;
                                    }
                                }                          

                                ` }>
                            </bunchball-events> 

                        </div>


                        <div className='bb tall'>

                        { leaderboardConfig?.length > 0 && 
                            <bunchball-leaders 
                                data-token={ dataToken }
                                data-server={ BUNCHBALL_ENDPOINT }
                                data-user-id={ claims.person_id }
                                data-leaderboard-configs={ leaderboardConfig }
                                data-leaderboard-configs-collection={ leaderboardConfig }
                                data-page-size='100'
                                data-language={ locale }
                                data-translation-reference={ translationKey }
                                data-subtitle={ getString( 'progress.leaders.subtitle' ) }
                                data-inline-css={ `
                                ${ inlineStyle }

                                .leaderboard {
                                    padding: 0 30px;
                                    overflow-y: auto;
                                    max-height: 500px;
                                }

                                .leaderboard .grid-row img {
                                    max-width: 4rem;
                                    width: 100%;
                                    max-height: 4rem;
                                    height: 100%;
                                    object-fit: cover;
                                }

                                .leaderboard .grid-container.grid-row .grid-x.grid-margin-x .cell.text-center {
                                    width: 100%;
                                }
                                
                                .leaderboard .grid-x > .cell{
                                    font-size: 16px;
                                    font-weight: bold;
                                }

                                .leaderboard-profile-section > div > div > div:first-child {
                                    align-items: center;
                                }

                                .leaderboard-profile-section .name {
                                    font-weight: bold;
                                    font-size: 20px;
                                }
                                
                                .leaderboard-profile-section .value {
                                    font-weight: normal;
                                    font-size: 16px;
                                }

                                .grid-container > div > .cell:nth-child(1) {
                                    width: 3ch;
                                }
                                
                                .grid-container > div > .cell:nth-child(2) {
                                    padding-right: 5px;
                                    flex-shrink: 0;    
                                    width: auto;
                                    min-width: 5rem;
                                }
                                
                                .grid-container > div > .cell:nth-child(3) {
                                    max-width: 70%;
                                    flex-grow: 1;
                                    flex-shrink: 1; 
                                }

                                .grid-container > div > .cell:last-child {
                                    width: 5ch;
                                }

                                .leaderboard-profile-section > .columns > .row {
                                    padding-left: 30px;
                                }

                                .leaderboard-profile-section .row > .columns {
                                    padding: 0;
                                }

                                @media only screen and (max-width: 450px) {
                                    div.leaderboard {
                                        padding: 0 2px;
                                    }

                                    .leaderboard-profile-section div.row {
                                        gap: 0.5rem;
                                    }

                                    .leaderboard img {
                                        width: 3rem;
                                    }

                                    .leaderboard .grid-x {
                                        flex-wrap: nowrap;
                                    }
                                }
                                ` }>
                            </bunchball-leaders> 
                            }
                            
                        </div>           
                    </div>
                </>
                }

            </PageContainer>
        </PageContent>
    );
};

export default Progress;